
import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { OptimizedBlogPostCard } from "@/components/blog/OptimizedBlogPostCard";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  image_url: string | null;
  created_at: string;
  text_direction: "ltr" | "rtl";
}

const Blog = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user, hasRole } = useAuth();

  const canCreateBlog = user && (hasRole("admin") || hasRole("super_admin"));

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        // Transform the data to ensure text_direction is typed correctly
        const typedData = data?.map(post => ({
          ...post,
          text_direction: (post.text_direction as "ltr" | "rtl") || "ltr"
        }));
        
        setBlogPosts(typedData || []);
      } catch (error: any) {
        console.error('Error fetching blog posts:', error.message);
        toast({
          title: "Failed to load blog posts",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogPosts();
  }, [toast]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout title="Blog | Freelance With Andy" path="/blog">
      <div className="section-container">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold">
            <span className="text-[#34BF49]">Blog</span>
          </h1>
          {canCreateBlog && (
            <Link to="/blog/create">
              <Button className="flex items-center gap-2">
                <PlusCircle size={18} />
                Create Blog Post
              </Button>
            </Link>
          )}
        </div>
        <p className="text-xl text-gray-600 mb-10">
          Insights, tutorials, and thoughts on development, product management, and freelancing.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse">Loading blog posts...</div>
          </div>
        ) : blogPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <OptimizedBlogPostCard
                key={post.id}
                id={post.id}
                title={post.title}
                excerpt={post.excerpt}
                image_url={post.image_url}
                category={post.category}
                text_direction={post.text_direction}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600 mb-6">No blog posts found.</p>
            {canCreateBlog && (
              <Link to="/blog/create">
                <Button className="flex items-center gap-2 mx-auto">
                  <PlusCircle size={18} />
                  Create First Blog Post
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Blog;
